import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Bold, Italic, Heading1, List, Link2, MousePointerClick,
  Eye, Loader2, RotateCcw, Save,
} from "lucide-react";

/* Variable utilisée par le bouton d'action selon le type de mail */
const ACTION_VAR = {
  proposal_sent: "view_url",
  follow_up: "view_url",
  question: "internal_url",
  decision: "internal_url",
  invitation: "login_url",
};

/* Styles du contenu éditable (injectés une seule fois) */
const CONTENT_STYLE_ID = "oxi-email-editor-content-style";
const CONTENT_STYLE = `
.oxi-ee-content { font-size: 14px; line-height: 1.6; color: #1f2937; }
.oxi-ee-content:focus { outline: none; }
.oxi-ee-content > *:first-child { margin-top: 0; }
.oxi-ee-content > *:last-child { margin-bottom: 0; }
.oxi-ee-content h1 { font-size: 1.55rem; font-weight: 700; line-height: 1.25; margin: 0 0 .75rem; }
.oxi-ee-content p { margin: 0 0 .75rem; }
.oxi-ee-content p.eyebrow { font-size: .7rem; text-transform: uppercase; letter-spacing: .12em; font-weight: 600; color: #6b7280; margin-bottom: .35rem; }
.oxi-ee-content p.muted { color: #6b7280; font-size: .85rem; }
.oxi-ee-content strong { font-weight: 700; }
.oxi-ee-content em { font-style: italic; }
.oxi-ee-content a { color: hsl(var(--primary)); text-decoration: underline; }
.oxi-ee-content a.email-btn {
  display: inline-block; background: hsl(var(--primary)); color: #fff;
  text-decoration: none; padding: .6rem 1.15rem; border-radius: 6px;
  font-weight: 600; margin: .25rem 0;
}
.oxi-ee-content ul { list-style: disc; padding-left: 1.35rem; margin: 0 0 .75rem; }
.oxi-ee-content ol { list-style: decimal; padding-left: 1.35rem; margin: 0 0 .75rem; }
.oxi-ee-content li { margin: 0 0 .25rem; }
.oxi-ee-content blockquote { border-left: 3px solid hsl(var(--primary)); padding-left: .85rem; margin: 0 0 .75rem; color: #6b7280; font-style: italic; }
.oxi-ee-content:empty:before { content: attr(data-placeholder); color: #9ca3af; }
`;

function injectContentStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(CONTENT_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = CONTENT_STYLE_ID;
  el.textContent = CONTENT_STYLE;
  document.head.appendChild(el);
}

export default function EmailEditor({
  type, label, pixel, variables = [],
  subject: subjectProp = "", body: bodyProp = "",
  previewUrl, onSave, onReset, showHeader = true,
}) {
  const [subject, setSubject] = useState(subjectProp || "");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [preview, setPreview] = useState(null); // { subject, html }
  const [previewLoading, setPreviewLoading] = useState(true);

  const bodyRef = useRef(null);
  const previewTimer = useRef(null);
  const mounted = useRef(true);

  useEffect(() => { injectContentStyle(); }, []);

  // Sync objet quand la prop change (reset / changement de société)
  useEffect(() => { setSubject(subjectProp || ""); }, [subjectProp]);

  // Initialise / ré-initialise le contentEditable quand la prop body change,
  // uniquement s'il n'est pas focus (évite de casser le curseur en cours de frappe).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    const next = bodyProp || "";
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [bodyProp]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(previewTimer.current); };
  }, []);

  const getBody = () => bodyRef.current?.innerHTML || "";

  const fetchPreview = async () => {
    if (!previewUrl) return;
    try {
      const { data } = await api.post(previewUrl, { type, subject, body: getBody() });
      if (!mounted.current) return;
      setPreview({ subject: data.subject, html: data.html });
    } catch {
      // On garde l'aperçu précédent en cas d'échec, sans casser l'UI.
    } finally {
      if (mounted.current) setPreviewLoading(false);
    }
  };

  const schedulePreview = () => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(fetchPreview, 500);
  };

  // Aperçu initial + à chaque changement d'objet.
  useEffect(() => { schedulePreview(); /* eslint-disable-next-line */ }, [subject]);
  // eslint-disable-next-line
  useEffect(() => { fetchPreview(); }, []);

  // ─── Barre d'outils ───
  const exec = (command, value = null) => {
    const el = bodyRef.current;
    if (el && document.activeElement !== el) el.focus();
    try { document.execCommand(command, false, value); } catch { /* noop */ }
    schedulePreview();
  };

  const insertLink = () => {
    const url = window.prompt("URL du lien (https://…)", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const insertActionButton = () => {
    const labelText = window.prompt("Libellé du bouton d'action", "Voir la proposition");
    if (!labelText) return;
    const v = ACTION_VAR[type] || "view_url";
    const el = bodyRef.current;
    if (el && document.activeElement !== el) el.focus();
    const safe = labelText.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    document.execCommand("insertHTML", false, `<a class="email-btn" href="{{${v}}}">${safe}</a>`);
    schedulePreview();
  };

  const insertVar = (v) => {
    const el = bodyRef.current;
    if (el && document.activeElement !== el) el.focus();
    document.execCommand("insertText", false, `{{${v}}}`);
    schedulePreview();
  };

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave({ subject, body: getBody() });
      toast.success("Modèle enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || formatAxiosError(e));
    } finally { if (mounted.current) setSaving(false); }
  };

  const reset = async () => {
    if (!onReset) return;
    if (!window.confirm("Réinitialiser ce modèle sur le modèle global ? La surcharge sera supprimée.")) return;
    setResetting(true);
    try {
      await onReset();
      toast.success("Modèle réinitialisé");
    } catch (e) {
      toast.error(e.response?.data?.detail || formatAxiosError(e));
    } finally { if (mounted.current) setResetting(false); }
  };

  const toolBtn = "h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors";

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid={`email-editor-${type}`}>
      {/* ─── Colonne gauche : éditeur ─── */}
      <div className="space-y-4">
        {showHeader && (
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-heading text-base font-semibold">{label}</h4>
            {pixel && (
              <span className="text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Suivi d'ouverture inclus
              </span>
            )}
          </div>
        )}

        <div>
          <Label>Objet</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
            data-testid={`email-editor-subject-${type}`} placeholder="Objet de l'email" className="mt-1.5 rounded-sm" />
        </div>

        <div>
          <Label>Corps</Label>
          {/* Barre d'outils */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 border border-border rounded-t-sm bg-muted/40 p-1.5">
            <button type="button" title="Gras" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}
              data-testid="email-editor-tool-bold" className={toolBtn}><Bold className="w-4 h-4" /></button>
            <button type="button" title="Italique" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}
              data-testid="email-editor-tool-italic" className={toolBtn}><Italic className="w-4 h-4" /></button>
            <button type="button" title="Titre" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("formatBlock", "h1")}
              data-testid="email-editor-tool-heading" className={toolBtn}><Heading1 className="w-4 h-4" /></button>
            <button type="button" title="Liste à puces" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")}
              data-testid="email-editor-tool-list" className={toolBtn}><List className="w-4 h-4" /></button>
            <button type="button" title="Lien" onMouseDown={(e) => e.preventDefault()} onClick={insertLink}
              data-testid="email-editor-tool-link" className={toolBtn}><Link2 className="w-4 h-4" /></button>
            <button type="button" title="Bouton d'action" onMouseDown={(e) => e.preventDefault()} onClick={insertActionButton}
              data-testid="email-editor-tool-button"
              className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-sm border border-border text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors text-xs font-medium">
              <MousePointerClick className="w-4 h-4" /> Bouton
            </button>

            {variables.length > 0 && (
              <>
                <span className="w-px h-5 bg-border mx-1" />
                {variables.map((v) => (
                  <button key={v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar(v)}
                    data-testid={`email-editor-tool-var-${v}`}
                    className="h-7 px-2 rounded-sm border border-border bg-background text-[11px] font-mono text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-colors">
                    {`{{${v}}}`}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Zone éditable */}
          <div
            ref={bodyRef}
            contentEditable
            suppressContentEditableWarning
            onInput={schedulePreview}
            data-testid={`email-editor-body-${type}`}
            data-placeholder="Rédigez le corps de l'email…"
            className="oxi-ee-content min-h-[260px] w-full rounded-b-sm border border-t-0 border-border bg-background px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset overflow-auto"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Le style final (logo, couleurs, bouton brandé) est appliqué automatiquement à l'envoi — l'aperçu à droite montre le rendu réel.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={save} disabled={saving} data-testid={`email-editor-save-${type}`} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enregistrement…</> : <><Save className="w-4 h-4 mr-1.5" /> Enregistrer</>}
          </Button>
          {onReset && (
            <Button onClick={reset} disabled={resetting} variant="outline" data-testid={`email-editor-reset-${type}`} className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser</>}
            </Button>
          )}
        </div>
      </div>

      {/* ─── Colonne droite : aperçu live ─── */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <Eye className="w-4 h-4" /> Aperçu
          {previewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </div>
        <div className="rounded-sm border border-border bg-muted/30 overflow-hidden">
          <div className="border-b border-border bg-background px-4 py-2.5">
            <p className="text-[11px] text-muted-foreground">Objet</p>
            <p className="text-sm font-medium truncate">{preview?.subject || subject || "—"}</p>
          </div>
          <iframe
            title={`preview-${type}`}
            data-testid={`email-editor-preview-${type}`}
            srcDoc={preview?.html || "<p style='font-family:sans-serif;color:#9ca3af;padding:24px'>Aperçu en cours de chargement…</p>"}
            className="w-full h-[520px] bg-white"
            sandbox=""
          />
        </div>
      </div>
    </div>
  );
}
